import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import cache from '../utils/cache';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_API_URL || '';

// A session lasts one day. After that the user must log in again.
const SESSION_DURATION_MS = 24 * 60 * 60 * 1000;

const SESSION_EXPIRY_KEY = 'sessionExpiresAt';

// Marks a tab whose session came from the superadmin role switcher.
const IMPERSONATION_KEY = 'impersonation';

/**
 * Impersonated sessions live in sessionStorage, which is scoped to a single
 * browser tab. That is what lets the superadmin keep their own session in one
 * tab while a student dashboard runs in another - localStorage is shared across
 * every tab of the origin, so storing both there would make them overwrite each
 * other. Normal sign-ins keep using localStorage so they persist across
 * restarts and stay shared between tabs as before.
 */
const safeSessionStorage = (() => {
  try {
    const probe = '__odenta_ss_probe__';
    window.sessionStorage.setItem(probe, '1');
    window.sessionStorage.removeItem(probe);
    return window.sessionStorage;
  } catch (_) {
    return null;
  }
})();

const readSessionFrom = (store, { checkDailyExpiry }) => {
  if (!store) return null;

  const token = store.getItem('token');
  if (!token) return null;

  if (checkDailyExpiry) {
    const expiresAt = parseInt(store.getItem(SESSION_EXPIRY_KEY) || '0', 10);
    if (expiresAt && Date.now() > expiresAt) {
      // Daily session elapsed - treat as logged out.
      store.removeItem('token');
      store.removeItem('user');
      store.removeItem(SESSION_EXPIRY_KEY);
      return null;
    }
  }

  // An expired impersonation token cannot be refreshed, so drop it outright.
  try {
    const decoded = jwtDecode(token);
    if (decoded.exp && decoded.exp * 1000 <= Date.now() && decoded.impersonated) {
      store.removeItem('token');
      store.removeItem('user');
      store.removeItem(IMPERSONATION_KEY);
      return null;
    }
  } catch (_) {
    return null;
  }

  const storedImpersonation = store.getItem(IMPERSONATION_KEY);
  const impersonation = storedImpersonation ? JSON.parse(storedImpersonation) : null;

  const storedUser = store.getItem('user');
  if (storedUser) {
    return { token, user: JSON.parse(storedUser), impersonation };
  }

  const decoded = jwtDecode(token);
  return {
    token,
    impersonation,
    user: {
      id: decoded.id,
      studentId: decoded.studentId || decoded.id,
      role: decoded.role,
      university: decoded.university || '',
      name: decoded.name || 'Unknown',
    },
  };
};

// Read the persisted session synchronously so a freshly opened tab is already
// authenticated on its very first render (no bounce to /login).
// A tab-scoped (impersonated) session always wins over the shared one.
const readStoredSession = () => {
  try {
    const tabSession = readSessionFrom(safeSessionStorage, { checkDailyExpiry: false });
    if (tabSession) return tabSession;

    const shared = readSessionFrom(localStorage, { checkDailyExpiry: true });
    if (shared) return shared;

    return { token: null, user: null, impersonation: null };
  } catch (error) {
    return { token: null, user: null, impersonation: null };
  }
};

const initialSession = readStoredSession();

// Apply the header immediately at module load so requests fired during the very
// first render already carry the token.
if (initialSession.token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${initialSession.token}`;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(initialSession.user);
  const [token, setToken] = useState(initialSession.token);
  // Stop persisting refresh token in localStorage; backend uses HTTP-only cookie now
  const [refreshToken, setRefreshToken] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // True until the initial session has been validated. Route guards must wait
  // for this instead of redirecting on a momentarily-null user.
  const [initializing, setInitializing] = useState(Boolean(initialSession.token));
  // Set when this tab is running a superadmin role-switcher session.
  const [impersonation, setImpersonation] = useState(initialSession.impersonation || null);

  const isImpersonating = Boolean(impersonation);

  const logoutRef = useRef(null);
  const refreshPromiseRef = useRef(null);
  // Read inside listeners/effects that must not close over a stale value.
  const isImpersonatingRef = useRef(isImpersonating);
  isImpersonatingRef.current = isImpersonating;

  // Function to check if token is expired
  const isTokenExpired = useCallback((value, skewSeconds = 300) => {
    if (!value) return true;
    try {
      const decoded = jwtDecode(value);
      const currentTime = Date.now() / 1000;
      return decoded.exp < (currentTime + skewSeconds);
    } catch (error) {
      return true;
    }
  }, []);

  const clearSession = useCallback(() => {
    const wasImpersonating = isImpersonatingRef.current;

    setToken(null);
    setUser(null);
    setRefreshToken(null);
    setImpersonation(null);

    if (safeSessionStorage) {
      safeSessionStorage.removeItem('token');
      safeSessionStorage.removeItem('user');
      safeSessionStorage.removeItem(IMPERSONATION_KEY);
    }

    // Ending an impersonated tab must not sign the superadmin out of their own
    // tabs, so the shared session is left untouched.
    if (!wasImpersonating) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem(SESSION_EXPIRY_KEY);
    }

    delete axios.defaults.headers.common['Authorization'];
    // The cache now survives reloads, so it must be wiped on sign-out to keep
    // one account's data from showing up under the next one.
    cache.clear();
  }, []);

  // Function to refresh the access token. Concurrent callers share one request.
  const refreshAccessToken = useCallback(async () => {
    // Impersonated sessions are deliberately not refreshable - the refresh
    // cookie belongs to the superadmin, so refreshing here would hand this tab
    // a superadmin token. When the impersonation token expires, the tab ends.
    if (isImpersonatingRef.current) {
      if (logoutRef.current) logoutRef.current();
      else clearSession();
      return null;
    }

    if (refreshPromiseRef.current) return refreshPromiseRef.current;

    const run = (async () => {
      try {
        setIsRefreshing(true);
        const response = await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        const { token: newToken } = response.data;

        localStorage.setItem('token', newToken);
        setToken(newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

        return newToken;
      } catch (error) {
        console.error('Token refresh failed:', error);
        if (logoutRef.current) logoutRef.current();
        else clearSession();
        return null;
      } finally {
        setIsRefreshing(false);
        refreshPromiseRef.current = null;
      }
    })();

    refreshPromiseRef.current = run;
    return run;
  }, [clearSession]);

  // Function to setup axios interceptor for automatic token refresh
  const setupAxiosInterceptor = useCallback(() => {
    // Add response interceptor to handle 401 and 429 errors
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Handle 401 errors (unauthorized) - never retry the refresh call itself
        const isRefreshCall = originalRequest?.url?.includes('/api/auth/refresh');
        if (error.response?.status === 401 && !originalRequest._retry && !isRefreshCall) {
          originalRequest._retry = true;

          // Try to refresh the token
          const newToken = await refreshAccessToken();
          if (newToken) {
            // Retry the original request with new token
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return axios(originalRequest);
          }
        }

        // Handle 429 errors (rate limiting)
        if (error.response?.status === 429) {
          const retryAfter = error.response.headers['retry-after'] || 15;
          const errorMessage = error.response.data?.message ||
            `Too many requests. Please wait ${retryAfter} minutes before trying again.`;

          // Create a more user-friendly error
          const rateLimitError = new Error(errorMessage);
          rateLimitError.status = 429;
          rateLimitError.retryAfter = retryAfter;
          rateLimitError.isRateLimit = true;

          return Promise.reject(rateLimitError);
        }

        return Promise.reject(error);
      }
    );

    // Return cleanup function
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [refreshAccessToken]);

  useEffect(() => {
    if (token) {
      try {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // An impersonated session is written to this tab only; writing it to
        // localStorage would clobber the superadmin's own session.
        const store = isImpersonating ? safeSessionStorage : localStorage;
        store?.setItem('token', token);

        // Keep the user populated even while a refresh is in flight, so route
        // guards never see an authenticated-but-userless state.
        const storedUser = store?.getItem('user');
        if (storedUser) {
          setUser((prev) => prev || JSON.parse(storedUser));
        } else {
          const decoded = jwtDecode(token);
          setUser((prev) => prev || {
            id: decoded.id,
            studentId: decoded.studentId || decoded.id,
            role: decoded.role,
            university: decoded.university || '',
            name: decoded.name || 'Unknown',
          });
        }

        // Refresh in the background when the token is close to expiring.
        // Impersonated sessions never refresh (see refreshAccessToken).
        if (!isImpersonating && isTokenExpired(token)) {
          refreshAccessToken();
        }
      } catch (error) {
        console.error('Error decoding token:', error);
        clearSession();
      }
    } else {
      // Storage cleanup belongs to clearSession, which knows whether this tab
      // was a preview. Clearing it here ran *after* the impersonation flag had
      // already been reset, so ending a preview in one tab signed the
      // superadmin out of all of them.
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    }
    setInitializing(false);
  }, [token, isImpersonating, isTokenExpired, refreshAccessToken, clearSession]);

  // Setup axios interceptor when component mounts
  useEffect(() => {
    const cleanup = setupAxiosInterceptor();
    return cleanup;
  }, [setupAxiosInterceptor]);

  // Set up automatic token refresh before token expiry
  useEffect(() => {
    if (!token || isImpersonating) return;

    const refreshInterval = setInterval(() => {
      if (isTokenExpired(token)) {
        refreshAccessToken();
      }
    }, 10 * 60 * 1000); // check every 10 mins

    return () => clearInterval(refreshInterval);
  }, [token, isImpersonating, isTokenExpired, refreshAccessToken]);

  // End the session once the daily window elapses.
  // Impersonated tabs are bounded by their own token expiry instead.
  useEffect(() => {
    if (!token || isImpersonating) return;

    const check = () => {
      const expiresAt = parseInt(localStorage.getItem(SESSION_EXPIRY_KEY) || '0', 10);
      if (expiresAt && Date.now() > expiresAt) {
        clearSession();
      }
    };

    check();
    const sessionInterval = setInterval(check, 60 * 1000);
    return () => clearInterval(sessionInterval);
  }, [token, isImpersonating, clearSession]);

  // Keep every open tab of the app in sync: logging in or out in one tab
  // propagates to the others instead of leaving them stranded.
  //
  // An impersonated tab opts out entirely. It has its own tab-scoped session,
  // and reacting to localStorage would let the superadmin's session overwrite
  // the role being previewed - which is exactly what the feature must avoid.
  useEffect(() => {
    if (isImpersonating) return;

    const onStorage = (event) => {
      if (event.key === 'token') {
        if (event.newValue) {
          setToken(event.newValue);
          axios.defaults.headers.common['Authorization'] = `Bearer ${event.newValue}`;
        } else {
          setToken(null);
          setUser(null);
        }
      }
      if (event.key === 'user') {
        setUser(event.newValue ? JSON.parse(event.newValue) : null);
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [isImpersonating]);

  const login = async (newToken, userData) => {
    try {
      // Never let a previous account's cached responses leak into this session.
      cache.clear();
      setToken(newToken);

      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      localStorage.setItem('token', newToken);
      localStorage.setItem(SESSION_EXPIRY_KEY, String(Date.now() + SESSION_DURATION_MS));

      // Prefer the role the backend already returned with the login response;
      // only ask the server when it is missing (saves a request + a read).
      let role = userData.role;
      if (!role) {
        try {
          const res = await axios.get(`${API_URL}/api/auth/role`);
          if (res.data && res.data.role) {
            role = res.data.role;
          }
        } catch (roleErr) {
          console.warn('Could not fetch user role from backend:', roleErr);
        }
      }

      // Prioritize userData from backend response
      const updatedUser = {
        id: userData.id,
        studentId: userData.studentId || userData.id,
        name: userData.name || 'Unknown',
        university: userData.university || '',
        role: role || 'student',
      };

      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setInitializing(false);

      // Emit auth_event: login with device info
      try {
        const userAgent = navigator.userAgent;
        const pageUrl = window.location.href;
        await axios.post(`${API_URL}/api/student-tracking/track-student-activity`, {
          userId: updatedUser.studentId || updatedUser.id,
          userName: updatedUser.name,
          userRole: updatedUser.role,
          action: 'auth_event',
          details: JSON.stringify({ eventType: 'login' }),
          metadata: JSON.stringify({ pageUrl }),
          timestamp: new Date().toISOString(),
          pageUrl,
          userAgent
        }, { headers: { Authorization: `Bearer ${newToken}` } });
      } catch (e) {
        // non-fatal
      }
    } catch (error) {
      console.error('Login error:', error);
      throw new Error('Invalid token');
    }
  };

  const logout = useCallback(() => {
    const currentUser = user;
    const currentToken = token;
    // Fire and forget auth_event: logout
    try {
      const userAgent = navigator.userAgent;
      const pageUrl = window.location.href;
      if (currentUser && currentToken) {
        axios.post(`${API_URL}/api/student-tracking/track-student-activity`, {
          userId: currentUser.studentId || currentUser.id,
          userName: currentUser.name,
          userRole: currentUser.role,
          action: 'auth_event',
          details: JSON.stringify({ eventType: 'logout' }),
          metadata: JSON.stringify({ pageUrl }),
          timestamp: new Date().toISOString(),
          pageUrl,
          userAgent
        }, { headers: { Authorization: `Bearer ${currentToken}` } }).catch(() => {});
      }
    } catch (_) {}

    clearSession();
  }, [user, token, clearSession]);

  logoutRef.current = logout;

  /**
   * Adopt a superadmin role-switcher session in THIS TAB ONLY.
   *
   * Everything is written to sessionStorage, so the superadmin's own session in
   * other tabs is untouched and each impersonated tab is independent.
   */
  const beginImpersonation = useCallback(({ token: newToken, user: newUser, impersonation: info }) => {
    if (!safeSessionStorage) {
      throw new Error('This browser blocks per-tab storage, which this feature needs.');
    }

    // Drop anything cached for the superadmin so the new role starts clean.
    cache.clear();

    safeSessionStorage.setItem('token', newToken);
    safeSessionStorage.setItem('user', JSON.stringify(newUser));
    safeSessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(info || { active: true }));

    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

    setImpersonation(info || { active: true });
    setUser(newUser);
    setToken(newToken);
    setInitializing(false);
  }, []);

  /** Leave an impersonated tab. Only affects this tab. */
  const endImpersonation = useCallback(() => {
    clearSession();
  }, [clearSession]);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      refreshToken,
      login,
      logout,
      refreshAccessToken,
      isRefreshing,
      initializing,
      isImpersonating,
      impersonation,
      beginImpersonation,
      endImpersonation
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
